/**
 * Social systems — shared types + validation (spec:
 * docs/superpowers/specs/2026-07-18-social-systems-design.md).
 *
 * Phase 1 carried chat; Phase 2 adds the friends blob (SocialStore). Party
 * and guild shapes join in later phases. Everything here is pure string/
 * number work — no trig, no Date, no randomness (pmath-ban + determinism
 * rules); timestamps are always passed in.
 */

import { normalizeCharacterName } from "./creation.js";

/** Hard cap on one chat line (UTF-16 code units), enforced on BOTH ends. */
export const CHAT_MAX = 240;

export const CHAT_CHANNELS = ["local", "party", "guild", "direct"] as const;
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

/**
 * Sanitize one chat line. Chat is the FIRST free-text player content on the
 * wire — unlike character names there is no alphabet restriction, so this is
 * the choke point that keeps hostile text inert before it is relayed:
 *
 *   - CRLF/CR normalize to LF; tabs become spaces
 *   - C0/C1 controls, DEL, soft hyphen, and the invisible steering set
 *     (zero-width/joiners, bidi embedding/override/isolate marks, BOM/word
 *     joiner, arabic letter mark) are stripped — bidi controls especially,
 *     which can visually reverse a rendered line
 *   - runs of 3+ newlines collapse to 2 (no wall-of-blank-lines spam)
 *   - trimmed, then capped at CHAT_MAX without ever splitting a surrogate pair
 *
 * Returns null when nothing legible remains (or input isn't a string).
 * Display-safety only — rendering must STILL use textContent, never innerHTML.
 */
// --- Friends (Phase 2) ---

/** Friendship never auto-evicts; the cap only refuses NEW edges. */
export const FRIENDS_MAX = 100;
/** Pending requests (each direction) evict oldest when full. */
export const PENDING_MAX = 40;
export const BLOCKED_MAX = 100;

/** One edge of a (mutual) friendship, a pending request, or a block.
 *  `name` is a cached display copy refreshed whenever the server observes
 *  the peer online — the uid is the key, and it NEVER goes to clients. */
export interface FriendEdge {
  uid: string;
  name: string;
  /** When the edge was created (server ms, passed in — never read here). */
  at: number;
}

/** The per-account social blob — one JSON-string field `social` on the
 *  server state doc (the quests precedent). */
export interface SocialStore {
  friends: FriendEdge[];
  /** Requests awaiting MY answer. */
  incoming: FriendEdge[];
  /** Requests I sent, awaiting theirs. */
  outgoing: FriendEdge[];
  /** I never see their requests or whispers. */
  blocked: FriendEdge[];
  /** Presence privacy: false hides my SYSTEM from friends (not online-ness). */
  shareSystem: boolean;
  /** Guild membership pointer — the guild doc is authoritative (Phase 4). */
  guildId: string | null;
}

export function defaultSocial(): SocialStore {
  return { friends: [], incoming: [], outgoing: [], blocked: [], shareSystem: true, guildId: null };
}

/** Longest a cached display name can be (character names are 3–24). */
const EDGE_NAME_MAX = 24;

function sanitizeEdges(raw: unknown, cap: number, keepNewest: boolean): FriendEdge[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const edges: FriendEdge[] = [];
  // keepNewest (requests): scan from the tail so the cap evicts the oldest.
  // keepOldest (friends/blocked): scan from the head — standing never evicts.
  const src = keepNewest ? [...raw].reverse() : raw;
  for (const v of src) {
    if (edges.length >= cap) break;
    if (!v || typeof v !== "object") continue;
    const o = v as Record<string, unknown>;
    if (typeof o.uid !== "string" || o.uid.length === 0 || o.uid.length > 128) continue;
    if (seen.has(o.uid)) continue;
    seen.add(o.uid);
    const name = typeof o.name === "string" ? o.name.slice(0, EDGE_NAME_MAX) : "";
    const at = typeof o.at === "number" && Number.isFinite(o.at) && o.at >= 0 ? o.at : 0;
    edges.push({ uid: o.uid, name, at });
  }
  return keepNewest ? edges.reverse() : edges;
}

/**
 * Validate a persisted social blob (a JSON string, or an already-parsed
 * object). validateStyle discipline: per-field defaulting — a garbage field
 * falls back alone, the rest of the blob survives; unknown keys dropped.
 * Returns undefined for a missing/garbage whole so legacy docs round-trip
 * with no social field at all.
 */
export function sanitizeSocial(raw: unknown): SocialStore | undefined {
  let data: unknown = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;
  const o = data as Record<string, unknown>;
  return {
    friends: sanitizeEdges(o.friends, FRIENDS_MAX, false),
    incoming: sanitizeEdges(o.incoming, PENDING_MAX, true),
    outgoing: sanitizeEdges(o.outgoing, PENDING_MAX, true),
    blocked: sanitizeEdges(o.blocked, BLOCKED_MAX, false),
    shareSystem: o.shareSystem !== false, // default true
    guildId: typeof o.guildId === "string" && o.guildId.length > 0 && o.guildId.length <= 128 ? o.guildId : null,
  };
}

// --- Parties (Phase 3) ---

/** Party size cap, invites included at accept time. */
export const PARTY_MAX = 4;
/**
 * A disconnected member keeps their slot this long. Load-bearing: warping is
 * leave-then-rejoin with socket churn, so "socket closed" must NOT mean "left
 * the party" — 90 s comfortably covers a warp plus a slow reload.
 */
export const PARTY_GRACE_MS = 90_000;
/** An unanswered invite expires after this long. */
export const PARTY_INVITE_TTL_MS = 60_000;

// --- Guilds (Phase 4) ---

/** Roster cap for v1 — keeps the guild doc far under Firestore's 1 MB limit
 *  (no chunking, no subcollection). */
export const GUILD_MEMBERS_MAX = 50;
/** Founding a guild is a credits SINK (the buyUpgrade shape debits it). */
export const GUILD_CREATE_COST = 5000;
/** Message of the day: one line of the guild's own voice, shown at login. */
export const GUILD_MOTD_MAX = 140;
export const GUILD_TAG_MIN = 2;
export const GUILD_TAG_MAX = 4;
/** An unanswered guild invite expires after this long. */
export const GUILD_INVITE_TTL_MS = 60_000;

export type GuildRole = "leader" | "officer" | "member";

/** Rank order for the leader-leaves promotion chain (higher wins). */
export const GUILD_ROLE_RANK: Record<GuildRole, number> = { leader: 2, officer: 1, member: 0 };

export interface GuildMember {
  /** Cached display name (the guild doc is read without touching accounts). */
  name: string;
  role: GuildRole;
  /** Joined-at (server ms, passed in) — the seniority key for promotion. */
  at: number;
}

/** The guild document (`guilds/<id>`): server-owned, admin-SDK only. Members
 *  are a MAP keyed by uid — uids never leave the server. */
export interface GuildDoc {
  id: string;
  name: string;
  nameKey: string;
  tag: string;
  tagKey: string;
  createdAt: number;
  /** uid of the leader; always also present in `members` with role "leader". */
  leader: string;
  /** Officer-settable greeting — free player text, so it goes through
   *  sanitizeChatText on the way in and renders escaped on the way out. */
  motd?: string;
  members: Record<string, GuildMember>;
}

export type NameProblem = "required" | "length" | "characters";

/** Guild names share the character-name alphabet + length rules (3–24). */
export function normalizeGuildName(
  raw: unknown,
): { ok: true; display: string; key: string } | { ok: false; problem: NameProblem } {
  return normalizeCharacterName(raw);
}

/**
 * Guild tags are the over-head badge: 2–4 chars, `[A-Z0-9]`, uppercased, and
 * reserved uniquely. Deliberately stricter than names — this string renders
 * inside `[TAG] Name` over a player's head, so no spaces, no punctuation, no
 * confusable Unicode.
 */
export function normalizeGuildTag(
  raw: unknown,
): { ok: true; tag: string; key: string } | { ok: false; problem: NameProblem } {
  if (typeof raw !== "string") return { ok: false, problem: "required" };
  const t = raw.normalize("NFKC").trim().toUpperCase();
  if (t.length === 0) return { ok: false, problem: "required" };
  if (t.length < GUILD_TAG_MIN || t.length > GUILD_TAG_MAX) return { ok: false, problem: "length" };
  if (!/^[A-Z0-9]+$/.test(t)) return { ok: false, problem: "characters" };
  return { ok: true, tag: t, key: t.toLowerCase() };
}

function sanitizeRole(raw: unknown): GuildRole | null {
  return raw === "leader" || raw === "officer" || raw === "member" ? raw : null;
}

/**
 * Validate a persisted guild doc. All-or-nothing (unlike the per-field social
 * blob): a guild with a broken identity has no safe fallback — better to read
 * as "no guild" than to hand out a half-built roster. Unknown member entries
 * are dropped individually, and the leader is always seated as leader.
 */
export function sanitizeGuildDoc(raw: unknown): GuildDoc | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" && o.id.length > 0 && o.id.length <= 128 ? o.id : null;
  const name = normalizeGuildName(o.name);
  const tag = normalizeGuildTag(o.tag);
  const leader = typeof o.leader === "string" && o.leader.length > 0 ? o.leader : null;
  if (!id || !name.ok || !tag.ok || !leader) return null;
  const members: Record<string, GuildMember> = {};
  const rawMembers = o.members && typeof o.members === "object" && !Array.isArray(o.members)
    ? (o.members as Record<string, unknown>)
    : {};
  for (const [uid, v] of Object.entries(rawMembers)) {
    if (Object.keys(members).length >= GUILD_MEMBERS_MAX) break;
    if (uid.length === 0 || uid.length > 128 || !v || typeof v !== "object") continue;
    const m = v as Record<string, unknown>;
    const role = sanitizeRole(m.role);
    if (!role) continue;
    members[uid] = {
      name: typeof m.name === "string" ? m.name.slice(0, EDGE_NAME_MAX) : "",
      role,
      at: typeof m.at === "number" && Number.isFinite(m.at) && m.at >= 0 ? m.at : 0,
    };
  }
  // The leader row is load-bearing for every role check — repair it.
  if (!members[leader]) members[leader] = { name: "", role: "leader", at: 0 };
  else members[leader].role = "leader";
  const createdAt = typeof o.createdAt === "number" && Number.isFinite(o.createdAt) ? o.createdAt : 0;
  const motd = sanitizeChatText(o.motd)?.slice(0, GUILD_MOTD_MAX);
  return {
    id, name: name.display, nameKey: name.key, tag: tag.tag, tagKey: tag.key,
    createdAt, leader, ...(motd ? { motd } : {}), members,
  };
}

export function sanitizeChatText(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let s = raw.replace(/\r\n?/g, "\n").replace(/\t/g, " ");
  s = s.replace(/[\u0000-\u0009\u000B-\u001F\u007F-\u009F\u00AD\u061C\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g, "");
  s = s.replace(/\n{3,}/g, "\n\n").trim();
  if (s.length > CHAT_MAX) {
    s = s.slice(0, CHAT_MAX);
    // A cut that lands mid-astral-pair leaves a lone high surrogate — drop it.
    if (/[\uD800-\uDBFF]$/.test(s)) s = s.slice(0, -1);
    s = s.replace(/\s+$/, "");
  }
  return s.length > 0 ? s : null;
}
